import { z } from 'zod';

/**
 * Outside services the panel calls with keys the owner sets in the panel
 * (ADR-0048): not the environment, so connecting one needs no access to the
 * server. The secret half of each is sealed like a credential; the rest — a
 * login, an address, a model — may be shown.
 */

export const INTEGRATION_KINDS = ['google', 'anthropic', 'ahrefs', 'dataforseo', 'openpagerank'] as const;
export type IntegrationKind = (typeof INTEGRATION_KINDS)[number];

/** Claude models an article may be written by: both take the web search tool and structured output. */
export const CONTENT_MODELS = ['claude-opus-5', 'claude-sonnet-5'] as const;
export type ContentModelId = (typeof CONTENT_MODELS)[number];
export const DEFAULT_CONTENT_MODEL: ContentModelId = 'claude-opus-5';

const email = z.string().trim().toLowerCase().email();

/** A Google service account key as Google hands it out: the JSON file, pasted or uploaded. */
export const googleKeySchema = z
  .string()
  .trim()
  .min(2)
  .max(20_000)
  .transform((text, context) => {
    try {
      const key = JSON.parse(text) as Record<string, unknown>;
      if (key['type'] !== 'service_account' || typeof key['client_email'] !== 'string' || typeof key['private_key'] !== 'string') {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'Not a service account key' });
        return z.NEVER;
      }
      return text;
    } catch {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Not JSON' });
      return z.NEVER;
    }
  });

/**
 * What each integration takes. A secret left out of a change keeps the stored
 * one, so a model or a budget changes without the key being typed again.
 */
export const integrationInputSchemas = {
  google: z.object({
    key: googleKeySchema.optional(),
    /** Google accounts made owners of every property the panel verifies, so they see it in their own Search Console. */
    ownerEmails: z.array(email).max(5).default([]),
  }),
  anthropic: z.object({
    apiKey: z.string().trim().min(10).max(500).optional(),
    model: z.enum(CONTENT_MODELS).default(DEFAULT_CONTENT_MODEL),
    /** A month's spend the writer stops at, in US dollars. */
    monthlyBudgetUsd: z.number().int().min(1).max(100_000).default(50),
  }),
  dataforseo: z.object({
    login: z.string().trim().min(3).max(200),
    password: z.string().min(3).max(200).optional(),
  }),
  openpagerank: z.object({
    apiKey: z.string().trim().min(10).max(500).optional(),
  }),
  ahrefs: z.object({
    apiKey: z.string().trim().min(10).max(500).optional(),
    /** Monthly history of DR, referring domains and traffic: spends API units, so it can be turned off. */
    history: z.boolean().default(true),
  }),
} as const satisfies Record<IntegrationKind, z.ZodTypeAny>;

export type IntegrationInput<K extends IntegrationKind> = z.infer<(typeof integrationInputSchemas)[K]>;

/** The field of each input that is the secret: required the first time, optional after. */
export const INTEGRATION_SECRET_FIELD: Record<IntegrationKind, string> = {
  google: 'key',
  anthropic: 'apiKey',
  dataforseo: 'password',
  openpagerank: 'apiKey',
  ahrefs: 'apiKey',
};

/** What of an integration may be shown back, by kind. */
export interface IntegrationSettings {
  readonly google?: { readonly serviceAccount: string; readonly projectId: string | null; readonly ownerEmails: readonly string[] };
  readonly anthropic?: { readonly model: ContentModelId; readonly monthlyBudgetUsd: number; readonly keyHint: string };
  readonly dataforseo?: { readonly login: string };
  readonly openpagerank?: { readonly keyHint: string };
  readonly ahrefs?: { readonly keyHint: string; readonly history: boolean };
}

/**
 * What the last check found, for the integration's page to draw: a balance,
 * units spent against a limit. Each kind fills its own fields.
 */
export interface IntegrationCheckData {
  /** Google: properties the service account sees. */
  readonly properties?: number;
  /** Claude: the model the key answered for. */
  readonly model?: string;
  /** DataForSEO: money left, in US dollars. */
  readonly balanceUsd?: number | null;
  /** Ahrefs: the plan, and API units this month. */
  readonly plan?: string | null;
  readonly unitsUsed?: number | null;
  readonly unitsLimit?: number | null;
  readonly unitsResetAt?: string | null;
  readonly keyExpiresAt?: string | null;
  /** Ahrefs: a free key reads only today's Domain Rating. */
  readonly freeKey?: boolean;
}

/** What happened to an integration: set, removed, checked by a person or by the schedule. */
export const INTEGRATION_EVENT_TYPES = ['set', 'removed', 'check', 'scheduled_check'] as const;
export type IntegrationEventType = (typeof INTEGRATION_EVENT_TYPES)[number];

/** How often the worker checks every set key, so a revoked key or an empty balance shows without anyone pressing «Проверить». */
export const INTEGRATION_CHECK_EVERY_MS = 6 * 60 * 60_000;

/** The last four characters of a key: enough to tell two keys apart, not enough to use one. */
export function keyHint(key: string): string {
  return key.length <= 8 ? '••••' : `••••${key.slice(-4)}`;
}
